import { z } from '@tasama/utils';

export const createProjectDetailsSchema = (t: (_key: string) => string) =>
  z.object({
    // Project code name
    projectCodeName: z
      .string()
      .min(1, t('errors.validation.projectCodeName.required'))
      .min(3, t('errors.validation.projectCodeName.min'))
      .max(100, t('errors.validation.projectCodeName.max')),

    // Project details/description - OPTIONAL as per AC2
    projectDetails: z
      .string()
      .max(1000, t('errors.validation.projectDetails.max'))
      .optional()
      .or(z.literal('')),

    // Expected launch date - OPTIONAL as per AC11
    expectedLaunchDate: z
      .string()
      .optional()
      .or(z.literal(''))
      .refine(
        (date) => {
          if (!date || date === '') return true; // Optional field

          // Validate DD/MM/YYYY format
          const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
          if (!dateRegex.test(date)) {
            return false;
          }

          // Parse DD/MM/YYYY format
          const parts = date.split('/').map(Number);
          const day = parts[0];
          const month = parts[1];
          const year = parts[2];

          // Validate parts exist
          if (day === undefined || month === undefined || year === undefined) {
            return false;
          }

          const selectedDate = new Date(year, month - 1, day);

          // Check if date is valid
          if (
            selectedDate.getDate() !== day ||
            selectedDate.getMonth() !== month - 1 ||
            selectedDate.getFullYear() !== year
          ) {
            return false;
          }

          // Check if date is in the future
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          return selectedDate >= today;
        },
        {
          error: t('errors.validation.expectedLaunchDate.future'),
        }
      ),
  });

export type ProjectDetailsFormValues = z.infer<
  ReturnType<typeof createProjectDetailsSchema>
>;

// Helper function to validate date format
export const isValidDateFormat = (date: string): boolean => {
  if (!date) return true; // Optional field

  const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
  if (!dateRegex.test(date)) return false;

  const parts = date.split('/').map(Number);
  const day = parts[0]!;
  const month = parts[1]!;
  const year = parts[2]!;
  const parsedDate = new Date(year, month - 1, day);

  return (
    parsedDate.getDate() === day &&
    parsedDate.getMonth() === month - 1 &&
    parsedDate.getFullYear() === year
  );
};

// Helper function to check if date is in the future
export const isFutureDate = (date: string): boolean => {
  if (!date) return true; // Optional field

  const parts = date.split('/').map(Number);
  const day = parts[0];
  const month = parts[1];
  const year = parts[2];

  if (day === undefined || month === undefined || year === undefined) {
    return false;
  }

  const selectedDate = new Date(year, month - 1, day);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return selectedDate >= today;
};

// Helper function to get date validation error message
export const getDateValidationError = (
  date: string,
  t: (_key: string) => string
): string | null => {
  if (!date) return null; // Optional field

  const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
  if (!dateRegex.test(date)) {
    return t('errors.validation.expectedLaunchDate.format');
  }

  const parts = date.split('/').map(Number);
  const day = parts[0]!;
  const month = parts[1]!;
  const year = parts[2]!;
  const selectedDate = new Date(year, month - 1, day);

  if (
    selectedDate.getDate() !== day ||
    selectedDate.getMonth() !== month - 1 ||
    selectedDate.getFullYear() !== year
  ) {
    return t('errors.validation.expectedLaunchDate.format');
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (selectedDate < today) {
    return t('errors.validation.expectedLaunchDate.mustBeFuture');
  }

  return null;
};
